import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { api } from "../lib/api";
import { getStoredUser } from "../components/RegisterModal";
import { Star, MessageSquare, AlertTriangle, Lightbulb, Send, CheckCircle2 } from "lucide-react";

const KINDS = [
  { id: "testimonial", icon: MessageSquare, label_pt: "Depoimento",     label_en: "Testimonial" },
  { id: "complaint",   icon: AlertTriangle, label_pt: "Reclamação",     label_en: "Complaint" },
  { id: "suggestion",  icon: Lightbulb,     label_pt: "Sugestão",       label_en: "Suggestion" },
];

export default function Reviews() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const user = getStoredUser();
  const [data, setData] = useState({ reviews: [], total: 0, avg_rating: 0, by_kind: {} });
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", rating: 5, kind: "testimonial", title: "", content: "", persona: user?.persona || "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const res = await api.get("/reviews");
    setData(res.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? data.reviews : data.reviews.filter((r) => r.kind === filter);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.title || form.content.length < 10) {
      setError(pt ? "Preencha todos os campos (mínimo 10 caracteres no conteúdo)." : "Fill all fields (min 10 chars in content).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", { ...form, lang });
      setSuccess(true);
      setForm({ ...form, title: "", content: "" });
      load();
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err?.response?.data?.detail || (pt ? "Não foi possível enviar." : "Could not send."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-reviews">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
          {pt ? "Transparência total" : "Full transparency"}
        </div>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight">
          {pt ? "Depoimentos e Reclamações" : "Testimonials & Complaints"}
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-neutral-400 text-lg">
          {pt
            ? "Mostramos os elogios E as reclamações. Sem filtro, sem censura. É assim que se constrói confiança."
            : "We show the praise AND the complaints. Unfiltered, uncensored. That's how trust is built."}
        </p>
      </div>

      {/* Stats bar */}
      <div className="mt-10 surface p-6 grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="reviews-stats">
        <Stat big={data.avg_rating.toFixed(1)} small={`/ 5`} label={pt ? "Média geral" : "Average rating"} />
        <Stat big={data.total} label={pt ? "Avaliações totais" : "Total reviews"} />
        <Stat big={data.by_kind.testimonial || 0} label={pt ? "Depoimentos" : "Testimonials"} />
        <Stat big={data.by_kind.complaint || 0} label={pt ? "Reclamações" : "Complaints"} color="text-red-400" />
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="reviews-filters">
        {[{ id: "all", label: pt ? "Todas" : "All" }, ...KINDS.map((k) => ({ id: k.id, label: pt ? k.label_pt : k.label_en }))].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`px-3 py-1.5 rounded-md border text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
              filter === f.id
                ? "bg-gold-500 border-gold-500 text-black"
                : "bg-white/5 border-white/10 text-neutral-400 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-[1fr_420px] gap-6">
        {/* List */}
        <div className="space-y-3" data-testid="reviews-list">
          {filtered.length === 0 && (
            <div className="surface p-8 text-center text-neutral-500">
              {pt ? "Ainda sem avaliações desta categoria. Seja o primeiro." : "No reviews in this category yet. Be the first."}
            </div>
          )}
          {filtered.map((r) => {
            const kind = KINDS.find((k) => k.id === r.kind);
            const KIcon = kind?.icon || MessageSquare;
            return (
              <div key={r.id} className="surface p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-md grid place-items-center border ${
                      r.kind === "complaint" ? "bg-red-500/10 border-red-500/30 text-red-400"
                      : r.kind === "suggestion" ? "bg-sky-500/10 border-sky-500/30 text-sky-400"
                      : "bg-gold-500/10 border-gold-500/30 text-gold-400"
                    }`}>
                      <KIcon size={15} />
                    </div>
                    <div>
                      <div className="font-display text-base">{r.title}</div>
                      <div className="text-xs text-neutral-500 font-mono">
                        {r.name}
                        {r.persona && ` · ${r.persona}`}
                        {` · ${new Date(r.created_at).toLocaleDateString(pt ? "pt-BR" : "en-US")}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 shrink-0">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={13}
                        className={i < r.rating ? "fill-gold-400 text-gold-400" : "text-neutral-700"}
                      />
                    ))}
                  </div>
                </div>
                <p className="mt-3 text-sm text-neutral-300 leading-relaxed">{r.content}</p>
              </div>
            );
          })}
        </div>

        {/* Form */}
        <div className="surface p-6 h-max sticky top-28" data-testid="reviews-form">
          <h3 className="font-display text-xl tracking-tight">
            {pt ? "Compartilhe sua experiência" : "Share your experience"}
          </h3>
          <p className="mt-2 text-sm text-neutral-400">
            {pt ? "Publicamos 100% do que chega — elogio, reclamação ou sugestão." : "We publish 100% of what arrives — praise, complaint or suggestion."}
          </p>

          <form className="mt-5 space-y-3" onSubmit={submit}>
            <div className="grid grid-cols-3 gap-2" data-testid="form-kind">
              {KINDS.map((k) => {
                const Icon = k.icon;
                const selected = form.kind === k.id;
                return (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => setForm({ ...form, kind: k.id })}
                    data-testid={`kind-${k.id}`}
                    className={`flex flex-col items-center gap-1 py-3 px-2 rounded-md border text-xs transition-colors ${
                      selected
                        ? "bg-gold-500/15 border-gold-500/60 text-gold-200"
                        : "bg-white/5 border-white/10 text-neutral-400 hover:border-white/20"
                    }`}
                  >
                    <Icon size={14} />
                    <span>{pt ? k.label_pt : k.label_en}</span>
                  </button>
                );
              })}
            </div>

            {/* Rating */}
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono block mb-1.5">
                {pt ? "Sua nota" : "Your rating"}
              </label>
              <div className="flex gap-1" data-testid="form-rating">
                {Array.from({ length: 5 }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setForm({ ...form, rating: i + 1 })}
                    data-testid={`star-${i + 1}`}
                    className="p-1"
                  >
                    <Star
                      size={26}
                      className={i < form.rating ? "fill-gold-400 text-gold-400" : "text-neutral-600 hover:text-neutral-500"}
                    />
                  </button>
                ))}
              </div>
            </div>

            <input
              placeholder={pt ? "Seu nome" : "Your name"}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold-500"
              data-testid="form-name"
            />
            <input
              type="email"
              placeholder="E-mail"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold-500"
              data-testid="form-email"
            />
            <input
              placeholder={pt ? "Título (ex.: A IA acertou em cheio)" : "Title (e.g.: The AI nailed it)"}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold-500"
              data-testid="form-title"
            />
            <textarea
              rows={4}
              placeholder={pt ? "Conte sua experiência…" : "Tell us your experience…"}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold-500 resize-none"
              data-testid="form-content"
            />
            {error && <div className="text-xs text-red-400 font-mono">{error}</div>}
            {success && (
              <div className="text-xs text-gold-400 font-mono flex items-center gap-1.5">
                <CheckCircle2 size={13} /> {pt ? "Publicado! Obrigado." : "Published! Thank you."}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="w-full bg-gold-500 hover:bg-gold-400 text-black font-medium py-3 rounded-md disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <Send size={14} />
              {submitting ? "…" : pt ? "Publicar" : "Publish"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ big, small, label, color }) => (
  <div>
    <div className="flex items-baseline gap-1">
      <span className={`font-mono text-3xl ${color || "text-white"}`}>{big}</span>
      {small && <span className="font-mono text-sm text-neutral-500">{small}</span>}
    </div>
    <div className="mt-1 text-[10px] text-neutral-500 uppercase tracking-widest font-mono">{label}</div>
  </div>
);
